/**
 * One attachment, uploaded: the bytes, then the thumbnail the browser made.
 *
 * Two requests because the thumbnail does not fit in a header and the body is
 * the file. The thumbnail is best-effort — if it fails the grid draws the
 * type badge (or, for a picture, loads the picture itself) — so its failure
 * never fails the upload.
 */

import { patientsApi } from '../../../api/endpoints';
import { makeThumbnail } from '../../../components/common/thumbnails';
import { validateFile } from '../../../domain/attachments';

/**
 * @param {number} patientId
 * @param {File} file
 * @param {object} [meta]  { category, title, serviceRequestId, onProgress, signal }
 * @returns {Promise<object>} the attachment row, with its thumbnail when one was made
 */
export async function uploadAttachment(patientId, file, meta = {}) {
  const problem = validateFile(file);
  if (problem) throw new Error(problem);
  // Started alongside the upload: decoding a photo takes as long as sending it.
  const thumbnail = makeThumbnail(file).catch(() => null);
  const row = await patientsApi.uploadFile(patientId, file, meta);
  const picture = await thumbnail;
  if (!picture) return row;
  try {
    return await patientsApi.setAttachmentThumbnail(row.id, picture);
  } catch {
    return row;
  }
}

/* A small cache of fetched bytes, so the grid's lazy picture and the viewer
   opened from it do not download the same file twice. Only small files are
   kept, and only a few: a 50 MB video stays out of memory once closed. */
const CACHE_LIMIT = 16;
const CACHEABLE_BYTES = 10 * 1024 * 1024;
const cache = new Map();

/** The attachment's bytes as a Blob. */
export function attachmentBlob(row) {
  const key = Number(row.id);
  if (cache.has(key)) return cache.get(key);
  const promise = patientsApi.attachmentBlob(key);
  if (!row.sizeBytes || row.sizeBytes <= CACHEABLE_BYTES) {
    cache.set(key, promise);
    promise.catch(() => cache.delete(key));
    while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
  }
  return promise;
}

/** Forget a deleted attachment's bytes. */
export const forgetAttachment = (id) => cache.delete(Number(id));

/* The patient lists, shared by every request card of the same patient on a
   department's queue — ten cards, one request. Short-lived on purpose: a
   file another desk just uploaded should appear on the next look. */
const LIST_TTL_MS = 20 * 1000;
const lists = new Map();

export function patientAttachments(patientId, { fresh = false } = {}) {
  const key = Number(patientId);
  const hit = lists.get(key);
  if (!fresh && hit && Date.now() - hit.at < LIST_TTL_MS) return hit.promise;
  const promise = patientsApi.attachments(key);
  lists.set(key, { at: Date.now(), promise });
  promise.catch(() => lists.delete(key));
  return promise;
}

/** Drop a patient's cached list after an upload or a delete. */
export const invalidatePatientAttachments = (patientId) => lists.delete(Number(patientId));
